import { ROUTE_SIGN_IN } from '@/constants/routes';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useFunnelLead } from '@/hooks/useFunnelLead';
import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/**
 * Paused-memberships email capture (per the disable-signups Lovable mock).
 * Content only — host pages provide their own chrome (header, background).
 * Must render inside a .dashboard-reset ancestor for Tailwind utilities to
 * beat the Chakra element resets.
 */
export function NewMembershipsEmailCapture() {
  const { updateLead } = useFunnelLead();
  const analytics = useAnalyticsCalls();

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = email.trim();
    if (!EMAIL_REGEX.test(value)) {
      setError('Enter a valid email address.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    // Same identity-merge story as JoinStep1: email lands as an Amplitude
    // user property so Iterable can create a contact; no setUserId.
    analytics.callIdentifyFunnelLead({ email: value });
    try {
      await updateLead({ email: value });
      analytics.callFunnelFieldCaptured({ field: 'email', value, positioningVariant: 'memberships-paused' });
      setSubmittedEmail(value);
    } catch {
      // Mint/PATCH failures are already reported to Sentry inside useFunnelLead.
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="text-catch-dark-blue-90">
      {submittedEmail ? (
        <div className="flex flex-col items-center gap-5 rounded-2xl border border-solid border-[#E3DCCE] bg-white px-6 py-10 text-center">
          <CheckCircle2 className="h-14 w-14 text-catch-salmon" strokeWidth={1.75} aria-hidden="true" />
          {/* Chakra's unlayered p/h margins beat Tailwind's mt-* utilities here,
              so vertical rhythm comes from flex gaps, not margins. */}
          <div className="flex flex-col gap-3">
            <h1 className="text-[26px] leading-tight font-bold">You&apos;re on the list</h1>
            <p className="text-[15px] leading-relaxed text-catch-dark-blue-90-50">
              We&apos;ll send updates to{' '}
              <span className="font-semibold break-all text-catch-dark-blue-90">{submittedEmail}</span>
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4 text-center">
            <h1 className="text-[28px] leading-[1.15] font-bold">
              We&apos;ve paused new memberships while we work on what&apos;s next
            </h1>
            <p className="text-[15px] leading-relaxed text-catch-dark-blue-90-50">
              Leave your email and we&apos;ll let you know when we have an update.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-3" noValidate>
            <label htmlFor="notify-email" className="sr-only">
              Email
            </label>
            <input
              id="notify-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              maxLength={255}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) setError(null);
              }}
              className="h-12 w-full rounded-full border border-solid border-[#D8D0C3] bg-white px-5 text-base text-catch-dark-blue-90 placeholder:text-catch-dark-blue-90-40 focus:outline-none focus:ring-2 focus:ring-catch-light-blue"
            />
            {error && (
              <p role="alert" className="text-center text-sm text-catch-danger">
                {error}
              </p>
            )}
            {/* Shorthand padding on buttons inside .dashboard-reset — see the
                iOS Safari revert-layer note in dashboard-reset.css. */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="h-12 w-full rounded-full bg-catch-light-blue p-[0px_24px] text-base font-semibold text-catch-dark-blue-90 transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-catch-light-blue focus:ring-offset-2 disabled:opacity-60"
            >
              {isSubmitting ? 'Submitting…' : 'Notify Me'}
            </button>
          </form>
        </>
      )}
      <div className="mt-8 text-center text-sm text-catch-dark-blue-90-50">
        Existing member?{' '}
        <Link to={ROUTE_SIGN_IN} className="font-semibold text-catch-dark-blue-90 underline">
          Sign in
        </Link>
      </div>
    </div>
  );
}
